// ── User preferences module ───────────────────────────────────────────
// Fetches `/api/v1/users/me`, applies the user's `theme` and `units`
// to the document, and exposes typed helpers for the profile page.

import { setUnits, type UnitSystem } from './units.ts';
import { api_v1_users_me_path, api_v1_users_me_preferences_path } from './api-routes';
import { authenticatedFetch } from './utils.ts';
import { updateStoredUser } from './auth.ts';

export type Theme = 'light' | 'dark' | 'auto';

export interface UserPreferences {
  language: string;
  country: string | null;
  units: UnitSystem;
  theme: Theme;
}

const THEME_STORAGE_KEY = 'bp-theme';

/**
 * Apply a theme preference to `<html>`. Persists to localStorage so the
 * shared _theme_script.html.erb picks it up on the next page load.
 */
export function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else if (theme === 'light') {
    root.classList.remove('dark');
  } else {
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    root.classList.toggle('dark', !!prefersDark);
  }
  try {
    if (theme === 'auto') {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  } catch {
    // storage disabled — ignore
  }
}

/**
 * Persist the user's preferred unit system locally so planner/BOM/hydraulics
 * modules can read it without re-fetching the user record.
 */
// Delegates to `shared/units.ts` rather than keeping a second key. This module
// used to write `user-units` while `units.ts` read `smartpluvia-units`, so the
// preference fetched from the server was persisted where nothing looked for
// it. `getStoredUnits` went with the key — it had no callers outside its own
// test, and `getUnits()` is the reader.
export function persistUnits(units: UnitSystem) {
  setUnits(units);
}

export class UnauthorizedError extends Error {
  constructor() {
    super('Unauthorized');
    this.name = 'UnauthorizedError';
  }
}

/**
 * Fetch the current user's profile + preferences from the API.
 *
 * Returns `null` only when the caller has no JWT (true anonymous
 * session). A 401 from the server throws `UnauthorizedError` so the
 * caller can distinguish "not logged in" from "logged in but token
 * revoked". Other non-OK responses throw a generic Error so a flaky
 * network cannot silently redirect the user to /login.
 */
export async function fetchCurrentUser(): Promise<{ preferences: UserPreferences } | null> {
  // No pre-gate on isAuthenticated — anonymous callers get a 401 from
  // the server and the UnauthorizedError raise distinguishes that from
  // "not signed in at all" via the throw vs. null contract. Saves us
  // from coupling preference fetch to the localStorage-based auth proxy.
  const resp = await authenticatedFetch(api_v1_users_me_path());
  if (resp.status === 401) throw new UnauthorizedError();
  if (!resp.ok) throw new Error(`fetchCurrentUser failed: ${resp.status}`);
  return resp.json();
}

/**
 * PATCH the current user's preferences. Returns the updated user record.
 */
export async function updatePreferences(
  patch: Partial<UserPreferences>
): Promise<{ preferences: UserPreferences } | null> {
  const resp = await authenticatedFetch(api_v1_users_me_preferences_path(), {
    method: 'PATCH',
    body: patch as Record<string, unknown>,
  });
  if (resp.status === 401) throw new UnauthorizedError();
  if (!resp.ok) {
    let message = `Failed to update preferences: ${resp.status}`;
    try {
      const data = await resp.json();
      if (typeof data?.error === 'string') message = data.error;
    } catch {
      // body was not JSON — keep status-based message
    }
    throw new Error(message);
  }
  const body = await resp.json();
  if (body?.preferences) {
    applyTheme(body.preferences.theme);
    persistUnits(body.preferences.units);
    // enforcePreferredLocale() redirects every page load by the cached
    // user's preferences.language; left stale, a language saved here was
    // undone by the next navigation.
    updateStoredUser(body);
  }
  return body;
}
